import { forwardRef, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { resolveComponentVariant } from '../componentContract'
import { Popover } from '../overlays/Popover'
import { ListboxPicker } from './ListboxPicker'
import { findTypeaheadIndex, isTypeaheadKey, optionPlainText, optionText, renderOptionIcon, useTypeahead } from './listboxTypeahead'
import { fieldControlClassName } from '../../boardui/fieldStyles'
import styles from './SelectMenu.module.css'

const WHITESPACE_PATTERN = /\s+/

const sizes = ['compact', 'default']
// More options than this get a search box, unless `searchable` says otherwise.
const SEARCH_THRESHOLD = 8
const dotTones = new Set(['neutral', 'success', 'warning', 'danger', 'info', 'action'])
const openKeys = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp'])

// A status dot: a tone (`success`, `warning`…), `true` for neutral, or any
// CSS colour (a `var(--wf-…)` token).
function StatusDot({ tone }) {
  const name = tone === true ? 'neutral' : tone
  const known = dotTones.has(name)
  return <span className={styles.dot} data-tone={known ? name : undefined} style={known ? undefined : { background: name }} aria-hidden="true" />
}

// The selected option's full text for the trigger's tooltip, since a long
// value is cut off with an ellipsis.
function optionTitle(option) {
  return optionPlainText(option) || undefined
}

// A <label>'s text as a screen reader reads it: without the required mark.
function labelText(node) {
  if (!node) return ''
  const copy = node.cloneNode(true)
  copy.querySelectorAll('[aria-hidden="true"]').forEach((hidden) => hidden.remove())
  return copy.textContent.trim()
}

// What `aria-labelledby` ids name, as their text joined.
function labelledByText(ids) {
  if (!ids || typeof document === 'undefined') return ''
  return ids.split(WHITESPACE_PATTERN).map((one) => labelText(document.getElementById(one))).filter(Boolean).join(' ')
}

function leadingOf(option) {
  if (!option) return null
  if (option.dot) return <StatusDot tone={option.dot} />
  return renderOptionIcon(option.icon, { 'aria-hidden': true })
}

/**
 * A single select drawn by the design system instead of the browser: a button
 * styled like Select (Controls) that opens a listbox in a popover (the WAI-ARIA
 * select-only combobox). Plain `value` / `onChange(value, option)`; `name` adds
 * a hidden input so it posts with a native form.
 *
 * Options are `{ value, label, hint?, group?, disabled?, icon?, dot?, text? }`
 * (see ListboxPicker); `dot` draws a status dot before the label, here and in
 * the trigger. Long lists get a search box (`searchable`, by default when there
 * are more than eight options).
 *
 * Naming: an `id` lets a `<label htmlFor>` (or FormField) name it; `aria-label`
 * or `aria-labelledby` name it directly; `label` names it only when there is
 * neither an id nor those, and always names the list (else the list takes the
 * button's name).
 *
 * Keys: on the button Enter, Space, ↓ and ↑ open the menu on the current value
 * and a letter opens it on the next option starting with it (with a search
 * box, it starts the search). In the menu ↑/↓ (and Home/End without a search
 * box) move, Enter picks, Escape closes, Tab closes and goes on to the next
 * field; picking or Escape returns focus to the button. Letters typed in quick
 * succession carry on from the button into the list.
 */
export const SelectMenu = forwardRef(function SelectMenu({
  value,
  onChange,
  options = [],
  label,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  id,
  name,
  placeholder = 'Select…',
  size = 'default',
  disabled = false,
  // Shows the value and stays in the tab order (aria-disabled), but never
  // opens: a view-only form.
  readOnly = false,
  required,
  searchable,
  searchPlaceholder = 'Search',
  emptyMessage = 'No matches',
  renderValue,
  className = '',
  menuWidth,
  placement = 'bottom-start',
  onKeyDown,
  ...props
}, ref) {
  const baseId = useId()
  const listId = `${baseId}-list`
  const surfaceId = `${baseId}-menu`
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // A menu that becomes disabled while open closes: it can't be picked from.
  if ((disabled || readOnly) && open) {
    setOpen(false)
    setQuery('')
  }
  // Set when the menu opens: the option it highlights, its label and width.
  const [opening, setOpening] = useState({ value: undefined, label: label || ariaLabel || 'Options', style: undefined })
  const typeahead = useTypeahead()
  const resolvedSize = resolveComponentVariant('SelectMenu size', size, sizes, 'default')
  const isSearchable = searchable ?? options.length > SEARCH_THRESHOLD
  const selected = options.find((option) => option.value === value)

  const setTriggerRef = (node) => {
    triggerRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }

  // `highlight`: the option to start on (undefined: the first).
  const openMenu = (highlight) => {
    if (disabled || readOnly) return
    const trigger = triggerRef.current
    const width = trigger?.offsetWidth
    const style = menuWidth != null
      ? { width: menuWidth, maxWidth: 'calc(100vw - 1rem)' }
      : (width ? { minWidth: `min(${width}px, calc(100vw - 1rem))` } : undefined)
    const fieldLabel = labelText(trigger?.labels?.[0])
    const name = label || ariaLabel || labelledByText(ariaLabelledBy) || fieldLabel || 'Options'
    setOpening({ value: highlight, label: name, style })
    setOpen(true)
  }
  const close = () => {
    setOpen(false)
    setQuery('')
  }
  const pick = (option) => {
    close()
    if (option.value !== value) onChange?.(option.value, option)
  }

  // A search started from the button keeps typing after its first letter.
  useLayoutEffect(() => {
    if (!open) return
    const input = menuRef.current?.querySelector('input')
    if (input) input.setSelectionRange(input.value.length, input.value.length)
  }, [open])

  const handleTriggerKeyDown = (event) => {
    onKeyDown?.(event)
    if (event.defaultPrevented || disabled || readOnly || open) return
    if (openKeys.has(event.key)) {
      event.preventDefault()
      openMenu(value)
      return
    }
    if (isTypeaheadKey(event)) {
      event.preventDefault()
      if (isSearchable) {
        setQuery(event.key)
        openMenu(undefined)
        return
      }
      const from = options.findIndex((option) => option.value === value)
      const index = findTypeaheadIndex(options, typeahead(event.key), from)
      openMenu(index >= 0 ? options[index].value : value)
    }
  }

  // Built only while the menu is open: a closed menu needs none of it.
  const term = query.trim().toLowerCase()
  const listOptions = useMemo(() => {
    if (!open) return []
    const shown = term
      ? options.filter((option) => `${optionText(option)} ${option.hint ?? ''}`.toLowerCase().includes(term))
      : options
    return shown.map((option) => (option.dot ? { ...option, icon: leadingOf(option) } : option))
  }, [open, options, term])

  const classes = [
    fieldControlClassName('selectTrigger', resolvedSize),
    styles.trigger,
    className,
  ].filter(Boolean).join(' ')
  const leading = leadingOf(selected)

  return (
    <>
      <button
        {...props}
        ref={setTriggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup={isSearchable ? 'dialog' : 'listbox'}
        aria-expanded={open}
        aria-controls={open ? (isSearchable ? surfaceId : listId) : undefined}
        aria-label={ariaLabel ?? (ariaLabelledBy || id ? undefined : label)}
        aria-labelledby={ariaLabelledBy}
        aria-required={required || undefined}
        disabled={disabled}
        aria-disabled={readOnly || props['aria-disabled'] || undefined}
        data-readonly={readOnly || undefined}
        className={classes}
        data-open={open || undefined}
        data-value={selected ? String(value) : ''}
        onClick={(event) => {
          props.onClick?.(event)
          if (event.defaultPrevented) return
          if (open) close()
          else openMenu(value)
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        {leading ? <span className={styles.leading} aria-hidden="true">{leading}</span> : null}
        <span className={`${styles.value} ${selected ? '' : styles.placeholder}`.trim()} title={selected && props.title === undefined ? optionTitle(selected) : undefined}>
          {selected ? (renderValue ? renderValue(selected) : selected.label) : placeholder}
        </span>
        <ChevronDown size={16} className={styles.chevron} aria-hidden="true" />
      </button>
      {name ? <input type="hidden" name={name} value={value ?? ''} disabled={disabled} /> : null}
      <Popover
        open={open}
        onOpenChange={(next) => { if (!next) close() }}
        anchorRef={triggerRef}
        placement={placement}
        label={opening.label}
        role={isSearchable ? 'dialog' : null}
        id={surfaceId}
        contentClassName={styles.content}
        style={opening.style}
      >
        <div
          ref={menuRef}
          className={styles.menu}
          onKeyDown={(event) => { if (event.key === 'Tab') close() }}
        >
          <ListboxPicker
            label={opening.label}
            options={listOptions}
            isSelected={(optionValue) => optionValue === value}
            onPick={pick}
            searchable={isSearchable}
            query={query}
            onQueryChange={setQuery}
            searchLabel={`Search ${opening.label}`}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
            initialActiveValue={opening.value}
            listId={listId}
            typeahead={typeahead}
          />
        </div>
      </Popover>
    </>
  )
})
